import { ScrollCaptain } from 'scroll-captain';
import { getOffset } from '../../utilities/js/helper';
import './layer-teaser.scss';

class LayerTeaser {
    constructor (element, options) {
        // Default options
        const defaults = {
            initAttr: 'data-layerteaser',
            stickyValue: 'sticky',
            layerValue: 'layer-',
            scrollTriggerValue: 'scroll-trigger',
            containerValue: 'container',
            breakpoint: 768 // Minimum viewport width for different behavior
        };

        // Settings
        this.settings = Object.assign({}, defaults, options);

        // DOM elements
        this.$root = element;
        this.$container = this.$root.querySelector(`[${this.settings.initAttr}="${this.settings.containerValue}"]`);
        this.$$layers = this.$root.querySelectorAll(`[${this.settings.initAttr}^="${this.settings.layerValue}"]`);

        // Variables
        this.breakpoint = window.matchMedia(`(min-width: ${this.settings.breakpoint}px)`);
        this.breakpointActive = this.breakpoint.matches;
        this.animation = null;
        this.slider = null;
        this.highestHeight = 0;
        this.progress = 0;
        this.activeSlideIndex = 0;
    }

    initialize () {
        // Initialize the component if there are layers to process
        if (this.$$layers.length > 0) {
            this.setLayersHeight();
            this.loadAnimation();
            this.setEvents();
        }
    }

    setLayersHeight () {
        this.highestHeight = 0;

        // Find the highest layer to set a consistent minimum height
        this.$$layers.forEach(($layer, index) => {
            const height = $layer.clientHeight;

            if (height > this.highestHeight) {
                this.highestHeight = height;
            }
        });

        // Apply the highest layer height as a CSS variable
        this.$root.style.setProperty('--layer-min-height', `${this.highestHeight}px`);
        this.$root.style.setProperty('--layer-padding-bottom', `${(this.highestHeight + 32) * (this.$$layers.length)}px`);
    }

    loadAnimation () {
        const layerAnimations = {};
        const n = this.$$layers.length;

        this.$$layers.forEach(($layer, index) => {
            if (index > 0) {
                const prevEndProgress = Math.round((100 / n) * index);
                const endProgress = Math.round((100 / n) * (index + 1));

                layerAnimations[`animateLayer${index}`] = {
                    targetSuffix: `${this.settings.layerValue}${index + 1}`,
                    onscroll: true,
                    easing: 'linear',
                    duration: 0,
                    translateY: {
                        0: 0,
                        ...(index > 1 && {
                            [prevEndProgress]: $layer.clientHeight * (index - 1) * -1 + 32
                        }),
                        [endProgress]: $layer.clientHeight * index * -1
                    }
                };
            }
        });

        this.animation = new ScrollCaptain(this.$root, {
            initAttr: this.settings.initAttr,
            triggerSuffix: this.settings.scrollTriggerValue,
            cssSpace: 'layer-teaser-spacing',
            bottom: '-100%',
            top: `${n * 0.5 * -50}%`,
            animateSticky: {
                targetSuffix: this.settings.stickyValue,
                sticky: true
            },
            updateOnResize: () => {
                // Update breakpoint state on window resize
                this.breakpointActive = this.breakpoint.matches;
            },
            ...layerAnimations
        });
    }

    setEvents () {
        // Handle custom event for detecting programmatic scrolling
        window.addEventListener('programmaticScrollDetected', () => {
            const rootPosition = getOffset(this.$root);
            const scrollPosition = window.scrollY || document.documentElement.scrollTop;

            if (this.progress === 0 && rootPosition.top - scrollPosition < 0 && this.animation !== null) {
                this.animation.update();
            }
        });
    }
}

export { LayerTeaser };

window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    if ($context) {
        const $$roots = $context.querySelectorAll('[data-layerteaser="root"]');
        $$roots.forEach($root => {
            const $rootAPI = new LayerTeaser($root);
            $rootAPI.initialize();
        });
    }
});
